import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import UndoRedo from './UndoRedo';
import SearchBar from './SearchBar';
import LockControl from '../LockControl';
import {
  MagnifyingGlassIcon,
  CloudArrowUpIcon,
  ClockIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { getRelativeTime } from '../../utils/timeFormatter';

/**
 * Main Toolbar Component
 * 
 * Top toolbar with all editing controls
 */
const Toolbar = ({
  // Undo/Redo
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  
  // Search
  onSearch,
  onReplace,
  onNextSearch,
  onPreviousSearch,
  searchResults,
  currentSearchIndex,
  
  // Save
  onSave,
  isSaving,
  lastSaved,
  saveError,
  isDirty,
  
  // Lock
  lockStatus,
  hasLock,
  onAcquireLock,
  onReleaseLock,
  onForceOverride,
  lockLoading,
  lockInitializing,
  
  // Navigation
  sessionTitle,
  backUrl,
  
  className = '',
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  /**
   * Handle search open
   */
  const handleSearchOpen = () => {
    setIsSearchOpen(true);
  };

  /**
   * Handle search close
   */
  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  /**
   * Get save status display
   */
  const getSaveStatus = () => {
    if (isSaving) {
      return (
        <div className="te-save-status">
          <ClockIcon className="h-3.5 w-3.5 animate-spin" />
          Saving...
        </div>
      );
    }

    if (saveError) {
      return (
        <div className="te-save-status te-save-status-error" title={saveError}>
          <ExclamationCircleIcon className="h-3.5 w-3.5" />
          Save failed
        </div>
      );
    }

    if (isDirty) {
      return (
        <div className="te-save-status te-save-status-dirty">
          <ClockIcon className="h-3.5 w-3.5" />
          Unsaved changes
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="te-save-status te-save-status-saved">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Saved {getRelativeTime(lastSaved)}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`te-toolbar ${className}`}>
      <div className="te-toolbar-row">
        {/* Left: Back + title */}
        <div className="te-toolbar-left">
          {backUrl && (
            <Link to={backUrl} className="te-back-link" title="Back to session details">
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Back
            </Link>
          )}
          {sessionTitle && (
            <h1 className="te-title" title={sessionTitle}>{sessionTitle}</h1>
          )}
        </div>

        {/* Middle: Lock */}
        <div className="te-toolbar-lock">
          <LockControl
            lockStatus={lockStatus}
            hasLock={hasLock}
            onAcquireLock={onAcquireLock}
            onReleaseLock={onReleaseLock}
            onForceOverride={onForceOverride}
            isLoading={lockLoading}
            isInitializing={lockInitializing}
          />
        </div>

        {/* Right: Actions */}
        <div className="te-toolbar-right">
          <UndoRedo
            onUndo={onUndo}
            onRedo={onRedo}
            canUndo={canUndo}
            canRedo={canRedo}
          />

          <button
            onClick={handleSearchOpen}
            className="te-btn te-btn-ghost"
            title="Search (Cmd+F)"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            Search
          </button>

          <span className="te-divider" />

          {getSaveStatus()}

          <button
            onClick={onSave}
            disabled={isSaving || !isDirty || !hasLock}
            className={`te-btn ${isDirty && !isSaving && hasLock ? 'te-btn-primary' : 'te-btn-secondary'}`}
            title={!hasLock ? "You need to acquire the lock to save" : "Save (Cmd+S)"}
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Save
          </button>
        </div>
      </div>

      <SearchBar
        isOpen={isSearchOpen}
        onClose={handleSearchClose}
        onSearch={onSearch}
        onReplace={onReplace}
        onNext={onNextSearch}
        onPrevious={onPreviousSearch}
        results={searchResults}
        currentIndex={currentSearchIndex}
      />
    </div>
  );
};

export default Toolbar;


